import { useAppSelector } from 'store/hooks';
import { Box, Button, Grid, Typography, styled } from '@mui/material';
import React from 'react';
import { Row } from 'ui/Flex';
import { LoyaltyTier } from '../model/loyalty-tier';
import {
    EPremiumLoyaltyConditionType,
    UserMetricDefinition,
    UserMetricListener,
    UserMetricListenerCondition
} from '../model';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { usePremiumLoyaltyAudienceRequests } from '../hooks/usePremiumLoyaltyAudienceRequests';
import { PremiumLoyaltyAudienceSelect } from '../PremiumLoyaltyAudienceSelect';
import { PremiumLoyaltyConditionRow } from '../PremiumLoyaltyConditionRow';

const AUDIENCE_SELECT_MESSAGE = `Choose an audience to proceed with creating the birthday scheme. Alternatively,
                            you can create a new audience by entering the desired title and clicking '+ Create
                            new'`;

const StyledAndContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    bottom: 0,
    left: '50%',
    transform: 'translate(-50%, 50%)',
    padding: theme.spacing(0, 2),
    color: theme.palette.divider
}));
const StyledConditionsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    position: 'relative'
}));
const StyledFormContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.background.paper
}));

interface TieredLoyaltyTierModalProps {
    open: boolean;
    onClose: () => void;
    tier?: LoyaltyTier;
    tiers?: LoyaltyTier[];
    metricDefinition?: UserMetricDefinition;
    onConditionAdd: (value: UserMetricDefinition, newAudienceId?: string) => void;
}

export const TieredLoyaltyTierModal: React.FC<TieredLoyaltyTierModalProps> = ({
    onClose,
    tier,
    metricDefinition,
    tiers = [],
    onConditionAdd
}) => {
    const { audiences } = useAppSelector(state => state.audiences);
    const { locations } = useAppSelector(state => state.locations);
    // ==========
    const locationOptions = React.useMemo(
        () => locations.map(item => ({ label: item.title, value: item._id })),
        [locations]
    );
    // ==========Audience input==============
    const [audienceId, setAudienceId] = React.useState<string>(tier?.id);
    const audienceOptions = React.useMemo(
        () => audiences?.map(item => ({ label: `${item.title} [${item._id}]`, value: item._id })) ?? [],
        [audiences]
    );
    const handleAudienceChange = React.useCallback((value: string) => {
        setAudienceId(value);
    }, []);
    const audiencesToExclude = React.useMemo(() => tiers.map(item => item.id), [tiers]);
    // =========================================
    const {
        loading,
        handleDelete,
        handleSubmitToAdd,
        handleSubmitToRemove,
        deleteModalOpen,
        handleDeleteModalClose,
        handleDeleteModalConfirm
    } = usePremiumLoyaltyAudienceRequests(onConditionAdd, metricDefinition, tier, audienceId);
    // =========================================
    const isOnlyMetricCondition = React.useCallback(
        (item: UserMetricListenerCondition, type: string) =>
            'metricId' in item &&
            (type === 'ADD_TO_SEGMENT'
                ? tier?.metricAddConditions?.length === 1
                : tier?.metricRemoveConditions?.length === 1),
        [tier?.metricAddConditions?.length, tier?.metricRemoveConditions?.length]
    );
    const renderCondition = React.useCallback(
        (listenerId: string, type: string) => (item: UserMetricListenerCondition) =>
            (
                <PremiumLoyaltyConditionRow
                    key={item._id}
                    item={item}
                    audienceOptions={audienceOptions}
                    locationOptions={locationOptions}
                    metrics={metricDefinition?.metrics}
                    onDelete={handleDelete(listenerId)}
                    loading={loading}
                    metricRequired={isOnlyMetricCondition(item, type)}
                />
            ),
        [
            audienceOptions,
            handleDelete,
            isOnlyMetricCondition,
            loading,
            locationOptions,
            metricDefinition?.metrics
        ]
    );
    const renderListener = React.useCallback(
        (item: UserMetricListener) => item.conditions.map(renderCondition(item._id, item.type)),
        [renderCondition]
    );
    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h6" color="primary">
                        {tier ? 'Edit tier' : 'New tier'}
                    </Typography>
                </Grid>
                <Grid sx={{ position: 'relative' }} size={12}>
                    <PremiumLoyaltyAudienceSelect
                        value={audienceId}
                        onChange={handleAudienceChange}
                        loading={loading}
                        readOnly={!!tier}
                        options={audienceOptions}
                        excludeAudiences={audiencesToExclude}
                    />
                </Grid>
                {!audienceId && (
                    <Grid size={12}>
                        <Typography>{AUDIENCE_SELECT_MESSAGE}</Typography>
                    </Grid>
                )}
                {!!audienceId && (
                    <React.Fragment>
                        <Grid size={12}>
                            <StyledConditionsContainer>
                                <Typography sx={{ mb: 1 }}>Add conditions</Typography>
                                {tier?.addListeners?.map(renderListener)}
                                {!tier?.addListeners?.length && (
                                    <Row valign="center">
                                        <ErrorOutlineIcon sx={{ mr: 1, fontSize: '28px' }} />
                                        <Typography>
                                            You have no add conditions yet. If you leave it blank, no
                                            customers will be added to this tier.
                                        </Typography>
                                    </Row>
                                )}
                                {!!tier?.addListeners?.length && (
                                    <StyledAndContainer>
                                        <Typography>AND</Typography>
                                    </StyledAndContainer>
                                )}
                            </StyledConditionsContainer>
                        </Grid>
                        <Grid size={12}>
                            <StyledFormContainer>
                                <PremiumLoyaltyConditionRow
                                    metrics={metricDefinition?.metrics}
                                    audienceOptions={audienceOptions}
                                    locationOptions={locationOptions}
                                    onSubmit={handleSubmitToAdd}
                                    loading={loading}
                                    defaultItem={
                                        !tier?.metricAddConditions?.length && {
                                            type: EPremiumLoyaltyConditionType.Metric
                                        }
                                    }
                                    key={`add-${metricDefinition?.updatedAt}`}
                                />
                            </StyledFormContainer>
                        </Grid>
                        <Grid size={12}>
                            <StyledConditionsContainer>
                                <Typography sx={{ mb: 1 }}>Remove conditions</Typography>
                                {tier?.removeListeners?.map(renderListener)}
                                {!tier?.removeListeners?.length && (
                                    <Row valign="center">
                                        <ErrorOutlineIcon sx={{ mr: 1, fontSize: '28px' }} />
                                        <Typography>
                                            You have no remove conditions yet. Please add one to ensure the
                                            customer isn&apos;t stuck in this tier.
                                        </Typography>
                                    </Row>
                                )}
                                {!!tier?.removeListeners?.length && (
                                    <StyledAndContainer>
                                        <Typography>AND</Typography>
                                    </StyledAndContainer>
                                )}
                            </StyledConditionsContainer>
                        </Grid>
                        <Grid size={12}>
                            <StyledFormContainer>
                                <PremiumLoyaltyConditionRow
                                    metrics={metricDefinition?.metrics}
                                    audienceOptions={audienceOptions}
                                    locationOptions={locationOptions}
                                    onSubmit={handleSubmitToRemove}
                                    loading={loading}
                                    defaultItem={
                                        !tier?.metricRemoveConditions?.length && {
                                            type: EPremiumLoyaltyConditionType.Metric
                                        }
                                    }
                                    key={`remove-${metricDefinition?.updatedAt}`}
                                />
                            </StyledFormContainer>
                        </Grid>
                    </React.Fragment>
                )}
                <Grid size={12}>
                    <Row flex={1} align="flex-end">
                        <Button onClick={onClose} variant="outlined">
                            Close
                        </Button>
                    </Row>
                </Grid>
            </Grid>
            <ActionDialog
                open={deleteModalOpen}
                onClose={handleDeleteModalClose}
                onConfirm={handleDeleteModalConfirm}
                title="Deleting condition"
                content="This change will affect a live Tier Scheme, are you sure?"
            />
        </React.Fragment>
    );
};
